import React from "react";
import { MinusIcon, PlusIcon } from "lucide-react";
import { priceTiers } from "../../data/markings";
import { Highlight } from "../Highlight";
import { StepHeading } from "../StepHeading";
import { formatEuro, getTier } from "../../utils/pricing";
import type { Configurator } from "../../hooks/useConfigurator";
interface QuantityStepProps {
  config: Configurator;
}
export function QuantityStep({
  config
}: QuantityStepProps) {
  const {
    product,
    state,
    update
  } = config;
  if (!product) return null;
  const qty = state.quantity;
  const tooLow = qty < product.minQty;
  const activeTier = getTier(qty);
  const presets = Array.from(new Set([product.minQty, 100, 250, 500, 1000])).filter(n => n >= product.minQty);
  const step = product.minQty >= 100 ? 50 : 10;
  function setQty(value: number) {
    update("quantity", Math.max(0, Math.min(100000, Math.round(value))));
  }
  return <section data-id="element-161" data-mp="3b82wxaufbe5t">
      <StepHeading title={<>
            Combien de <Highlight data-id="element-163" data-mp="224g6vanj6buw">pièces</Highlight> ?
          </>} subtitle={`Minimum de commande : ${product.minQty} pièces. Plus la quantité est importante, plus le prix unitaire baisse.`} data-id="element-162" data-mp="3b6rtjc4w48f8" />

      <div className="grid border border-line lg:grid-cols-[1fr_1fr]" data-id="element-164" data-mp="2bu9i2ajrgoym">
        <div className="border-b border-line p-6 md:p-8 lg:border-b-0 lg:border-r" data-id="element-165" data-mp="2bwm0spxcf3cq">
          <label htmlFor="qty" className="text-sm font-bold" data-id="element-166" data-mp="26w7yloifanmm">
            Quantité
          </label>
          <div className="mt-3 flex items-stretch border border-ink" data-id="element-167" data-mp="1j63wxmosoxy9">
            <button type="button" aria-label="Diminuer" onClick={() => setQty(qty - step)} className="flex w-16 items-center justify-center border-r border-ink transition-colors duration-150 ease-out hover:bg-mist" data-id="element-168" data-mp="1e7skr5b12bcv">
              <MinusIcon size={20} aria-hidden="true" data-id="element-169" data-mp="33kxhni5sdma6" />
            </button>
            <input id="qty" type="number" inputMode="numeric" min={product.minQty} value={qty || ""} onChange={e => setQty(Number(e.target.value))} aria-invalid={tooLow} aria-describedby="qty-help" className="w-full min-w-0 py-4 text-center text-4xl font-bold tabular-nums outline-none md:text-5xl" data-id="element-170" data-mp="2zkdkwfp4kkok" />
            <button type="button" aria-label="Augmenter" onClick={() => setQty(qty + step)} className="flex w-16 items-center justify-center border-l border-ink transition-colors duration-150 ease-out hover:bg-mist" data-id="element-171" data-mp="2a7o3gfl1x3zr">
              <PlusIcon size={20} aria-hidden="true" data-id="element-172" data-mp="1mibungtmyxxz" />
            </button>
          </div>
          <p id="qty-help" className={`mt-3 text-sm ${tooLow ? "font-bold text-cherry" : "text-muted"}`} data-id="element-173" data-mp="1rad1gn81bwgm">
            {tooLow ? `Il faut au moins ${product.minQty} pièces pour ce modèle.` : "Saisissez une quantité ou choisissez un palier."}
          </p>
          <div className="mt-6 flex flex-wrap gap-2" data-id="element-174" data-mp="yrfv1xixuyg6">
            {presets.map(n => <button key={n} type="button" onClick={() => setQty(n)} className={`rounded-full border px-4 py-2 text-sm font-bold tabular-nums transition-colors duration-150 ease-out ${qty === n ? "border-ink bg-mint" : "border-ink hover:bg-mist"}`} data-id="element-175" data-mp="r86wz0fvum6f">
                {n}
              </button>)}
          </div>
        </div>

        <div className="p-6 md:p-8" data-id="element-176" data-mp="3kglou5zpkja">
          <h2 className="text-sm font-bold" data-id="element-177" data-mp="3jdzgzjm469p">Prix dégressifs du produit nu</h2>
          <table className="mt-3 w-full text-sm" data-id="element-178" data-mp="3urt5zi266e3f">
            <thead data-id="element-179" data-mp="3qpeph4lz3qfb">
              <tr className="border-b border-ink text-left text-muted" data-id="element-180" data-mp="3qpy4odk8o0pn">
                <th className="py-2 font-normal" data-id="element-181" data-mp="3lqbt08k26siy">À partir de</th>
                <th className="py-2 text-right font-normal" data-id="element-182" data-mp="3lpsc8v65aa6i">Prix unitaire HT</th>
              </tr>
            </thead>
            <tbody data-id="element-183" data-mp="3gtbklqzqldoo">
              {priceTiers.map(t => {
              const active = t.min === activeTier.min && !tooLow;
              return <tr key={t.min} className={`border-b border-ink/15 ${active ? "bg-mint font-bold" : ""}`} data-id="element-184" data-mp="1pm5gdvm7y9dh">
                    <td className="px-2 py-3 tabular-nums" data-id="element-185" data-mp="1axhobzbl0hsa">{t.min} pcs</td>
                    <td className="px-2 py-3 text-right tabular-nums" data-id="element-186" data-mp="1fwlvkkey45fz">{formatEuro(product.basePrice * t.factor)}</td>
                  </tr>;
            })}
            </tbody>
          </table>
        </div>
      </div>
    </section>;
}